import styled, { css, keyframes } from "styled-components";

import { IconButton } from "../../../../../components/IconButton";
import { SelectPill } from "../../../../../components/MobileForm/styles";
import { ColumnAlign } from "../../../../../components/SortableColumnHeader";
import { columnHeaderCellStyles } from "../../../../../components/SortableColumnHeader/styles";
import {
  QUERIES,
  SPACING,
  SPACING_RESPONSIVE,
  FONTS,
  TOUCH_TARGET,
} from "../../../../../utils/constants";
import { SECTION_GAP } from "../../constants";
import { BaseButton } from "../../../../../components/Button";

// The card is unpadded so its bands and tables run full-bleed; each band carries this inset itself.
const SECTION_INSET = SPACING.md + SPACING.xs;
const SECTION_INSET_COMPACT = SPACING.md - SPACING.xxs;
const CARD_RADIUS = 12;
const TOOLBAR_CONTROL_HEIGHT = 34;
const TOOLBAR_SEARCH_HEIGHT = 36;
const TOOLBAR_SEARCH_WIDTH = 270;

export const sectionInsetInline = (
  property: "padding" | "margin" = "padding",
) => css`
  ${property}-inline: ${SECTION_INSET_COMPACT}px;

  @media ${QUERIES.tabletLargeAndUp} {
    ${property}-inline: ${SECTION_INSET}px;
  }
`;

// The card has a 1px border, so a row inside it rounds to the border's inner curve.
const INNER_RADIUS = CARD_RADIUS - 1;

// Shared shell for Contributions/Market updates/Holdings/Activity; section-specific pieces stay in that section's own styles.ts.
export const SectionCard = styled.section`
  display: flex;
  flex-direction: column;
  min-width: 0;
  margin-top: ${SECTION_GAP}px;
  background: var(--color-card-background);
  border: 1px solid var(--color-card-border);
  border-radius: ${CARD_RADIUS}px;
  box-shadow: var(--shadow-card-rest);
  font-family: ${FONTS.serif};
`;

export const SectionHeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${SPACING.md}px;
  padding-block: ${SPACING.md}px ${SPACING.sm + SPACING.xxs}px;
  ${sectionInsetInline()}

  @media ${QUERIES.tabletLargeAndUp} {
    padding-block: ${SPACING.md + SPACING.xxs}px ${SPACING.sm + SPACING.xs}px;
  }
`;

export const SectionTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
  min-width: 0;
`;

export const SectionTitle = styled.h2`
  margin: 0;
  font-family: ${FONTS.serif};
  font-size: var(--text-xxl);
  font-weight: 700;
  letter-spacing: -0.015em;
  color: var(--color-text-default);
`;

// `$expanded` belongs to the collapsible sections: they hide the subtitle while
// collapsed. A section without a toggle omits the prop and stays visible.
export const SectionSubtitle = styled.p<{ $expanded?: boolean }>`
  display: ${({ $expanded = true }) => ($expanded ? "block" : "none")};
  margin: 0;
  font-size: var(--text-base);
  font-style: italic;
  color: var(--color-text-secondary);
`;

// Whole title block is the toggle so the hit area matches what it discloses.
export const SectionHeaderToggle = styled(BaseButton)`
  appearance: none;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: flex-start;
  gap: ${SPACING.sm}px;
  text-align: left;
  color: inherit;
`;

export const SectionChevron = styled.span<{ $expanded: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-secondary);
  margin-top: ${SPACING.xxs}px;
  transition: transform 0.2s ease;
  transform: ${({ $expanded }) =>
    $expanded ? "rotate(0deg)" : "rotate(-90deg)"};
`;

export const SectionBadge = styled.span`
  flex-shrink: 0;
  padding: ${SPACING.xxs + SPACING.xxs / 2}px ${SPACING.sm + SPACING.xxs / 2}px;
  border: 1px solid var(--color-card-border);
  border-radius: 7px;
  background: var(--color-background-0);
  color: var(--color-text-secondary);
  font-size: var(--text-xs);
  font-weight: 600;
  white-space: nowrap;
`;

export const SectionTable = styled.div`
  display: flex;
  flex-direction: column;
`;

// The edge columns carry the card inset themselves, so the rules still run edge to edge.
export const sectionTableEdges = css`
  th:first-child,
  td:first-child {
    padding-left: ${SECTION_INSET}px;
  }

  th:last-child,
  td:last-child {
    padding-right: ${SPACING.sm}px;
  }

  tbody > tr:last-child {
    border-bottom: 0;
  }

  ${SectionCard} > :last-child > &:last-child tbody > tr:last-child {
    > td:first-child {
      border-bottom-left-radius: ${INNER_RADIUS}px;
    }

    > td:last-child {
      border-bottom-right-radius: ${INNER_RADIUS}px;
    }
  }
`;

export const SectionHeaderCell = styled.span<{ $align?: ColumnAlign }>`
  ${columnHeaderCellStyles}
`;

export const SectionCardList = styled.div`
  display: flex;
  flex-direction: column;
`;

export const SectionCardRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
  padding: ${SPACING.md}px ${SECTION_INSET_COMPACT}px;
  border-bottom: 1px solid var(--color-card-border);

  &:last-child {
    border-bottom: 0;
  }

  ${SectionCard} > :last-child > ${SectionCardList}:last-child > &:last-child {
    border-radius: 0 0 ${INNER_RADIUS}px ${INNER_RADIUS}px;
  }
`;

// Legacy sections keep the padded layout; the full-bleed bands belong to Holdings and Activity.
export const InsetSectionCard = styled(SectionCard)`
  gap: ${SPACING.md}px;
  padding: ${SPACING_RESPONSIVE.mobile.chartPadding}px;

  @media ${QUERIES.tabletAndUp} {
    padding: ${SPACING_RESPONSIVE.tablet.chartPadding}px;
  }

  @media ${QUERIES.laptopAndUp} {
    padding: ${SPACING_RESPONSIVE.desktop.chartPadding}px;
  }

  ${SectionHeaderRow}, ${SectionCardRow} {
    padding-inline: 0;
  }

  ${SectionHeaderRow} {
    padding-block: 0;
  }
`;

// Ruled off from the rows above, like the toolbar band.
export const SectionFooter = styled.div`
  padding-block: ${SPACING.sm + SPACING.xs}px;
  ${sectionInsetInline()}
  border-top: 1px solid var(--color-card-border);
`;

export const SectionNoticeInset = styled.div`
  padding-bottom: ${SPACING.sm + SPACING.xs}px;
  ${sectionInsetInline()}
`;

export const SectionMessage = styled.p`
  margin: 0;
  padding: ${SPACING.xl}px ${SPACING.md}px;
  color: var(--color-text-secondary);
  font-size: var(--text-md);
  text-align: center;
`;

const toolbarControlStyles = css`
  min-height: ${TOOLBAR_CONTROL_HEIGHT}px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-background-0);
  color: var(--color-text-secondary);
  font-size: var(--text-xs);
  font-weight: 600;
  box-shadow: none;

  &:hover {
    border-color: var(--color-primary);
    color: var(--color-text-default);
  }
`;

export const focusRing = css`
  outline: 0;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 4px var(--shadow-focus);
`;

// The selects are restyled here because the toolbar owns their look.
export const SectionToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${SPACING.sm}px;
  min-height: ${SPACING.xxl + SPACING.xs}px;
  padding: ${SPACING.sm}px ${SPACING.sm + SPACING.xs}px;
  border-top: 1px solid var(--color-card-border);
  border-bottom: 1px solid var(--color-card-border);
  background: var(--color-card-background);

  @media ${QUERIES.tabletLargeAndUp} {
    padding-inline: ${SECTION_INSET}px;
  }

  .select__control {
    ${toolbarControlStyles}
  }

  .select__control--is-focused {
    ${focusRing}
  }

  .select__single-value {
    color: inherit;
    font-size: var(--text-xs);
    font-weight: 600;
  }

  .select__indicator-separator {
    display: none;
  }

  .select__dropdown-indicator {
    padding: 0 ${SPACING.xs}px;
  }

  ${SelectPill} {
    ${toolbarControlStyles}
    padding: ${SPACING.xs + SPACING.xxs / 2}px ${SPACING.sm}px
      ${SPACING.xs + SPACING.xxs / 2}px ${SPACING.sm + SPACING.xxs}px;
    font-family: inherit;

    &:focus-visible {
      ${focusRing}
    }

    @media ${QUERIES.tabletLargeAndDown} {
      min-height: ${TOUCH_TARGET}px;
    }
  }
`;

export const SectionSearchField = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;

  @media ${QUERIES.tabletLargeAndUp} {
    width: min(${TOOLBAR_SEARCH_WIDTH}px, 100%);
  }
`;

export const SectionSearchIcon = styled.span`
  position: absolute;
  left: ${SPACING.sm + SPACING.xxs}px;
  display: inline-flex;
  color: var(--color-text-muted);
  pointer-events: none;

  & > svg {
    font-size: var(--text-md);
  }
`;

export const SectionSearchInput = styled.input`
  width: 100%;
  min-height: ${TOUCH_TARGET}px;
  padding: ${SPACING.xs + SPACING.xxs}px ${SPACING.xl}px
    ${SPACING.xs + SPACING.xxs}px ${SPACING.xl + SPACING.xxs}px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-background-0);
  color: var(--color-text-default);
  font-family: ${FONTS.serif};
  font-size: var(--text-sm);

  &::placeholder {
    color: var(--color-text-muted);
  }

  &:focus {
    ${focusRing}
  }

  @media ${QUERIES.tabletLargeAndUp} {
    min-height: ${TOOLBAR_SEARCH_HEIGHT}px;
  }
`;

export const SectionSearchClearButton = styled(IconButton)`
  position: absolute;
  right: ${SPACING.xxs}px;
  color: var(--color-text-muted);
  font-size: var(--text-md);
`;

export const SectionToolbarGroup = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${SPACING.sm}px;
`;

const SKELETON_ROW_HEIGHT = 44;
const EMPTY_STATE_MIN_HEIGHT = 220;

const pulse = keyframes`
  0%, 100% { opacity: 0.48; }
  50% { opacity: 0.88; }
`;

export const EmptyStateWrapper = styled.div`
  display: grid;
  min-height: ${EMPTY_STATE_MIN_HEIGHT}px;
  place-items: center;
  padding: ${SPACING.lg + SPACING.xs}px;
  text-align: center;
`;

export const EmptyStateIcon = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${SPACING.sm + SPACING.xxs}px;
  color: var(--color-text-muted);
`;

export const EmptyStateTitle = styled.h3`
  margin: 0;
  font-size: var(--text-lg);
  color: var(--color-text-default);
`;

export const EmptyStateDescription = styled.p`
  max-width: 42ch;
  margin: ${SPACING.xs}px auto 0;
  color: var(--color-text-secondary);
  font-size: var(--text-sm);
`;

export const EmptyStateAction = styled.div`
  margin-top: ${SPACING.sm + SPACING.xs}px;
`;

export const SkeletonRows = styled.div`
  padding: ${SPACING.sm + SPACING.xxs}px ${SPACING.md + SPACING.xs}px
    ${SPACING.md + SPACING.xxs}px;
`;

export const SkeletonRow = styled.div`
  height: ${SKELETON_ROW_HEIGHT}px;
  margin-top: ${SPACING.sm + SPACING.xxs / 2}px;
  border-radius: 8px;
  background: var(--gradient-skeleton);
  animation: ${pulse} 1.2s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
